import ImageIcon from "@mui/icons-material/Image"
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import { Alert, Box } from "@mui/material"
import Button from "@mui/material/Button"
import Container from "@mui/material/Container"
import Link from "@mui/material/Link"
import Typography from "@mui/material/Typography"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useLoaderData } from "react-router-dom"

import { finishRequest, getData, getDownloadUrl } from "../api/connect"
import { useCancelOnClose } from "../hooks/useCancelOnClose"
import { useDictionary } from "../hooks/useDictionary"
import { useInterval } from "../hooks/useInterval"

const copyToClipboard = async (value) => {
  await navigator.clipboard.writeText(value)
}

// Vault document paths may arrive as fetchurl, value, or valueString depending on API version.
const getDocumentPath = (item) => {
  if (!item) return null
  const path =
    item.fetchurl ??
    item.fetchUrl ??
    item.filename ??
    item.valueString ??
    item.value
  return typeof path === "string" && path ? path : null
}

const getDocumentLabel = (path) => ( "Download")

// Picks a file-type icon from the path's extension, mirroring the app's
// DisplayDocument fallback (PDF icon, image icon, otherwise a generic file).
const getDocumentIcon = (path) => {
  const ext = path?.split("?")[0].split(".").pop()?.toLowerCase()
  if (ext === "pdf") return PictureAsPdfIcon
  if (["jpg", "jpeg", "png", "gif", "heic", "heif", "webp"].includes(ext)) {
    return ImageIcon
  }
  return InsertDriveFileIcon
}

function DataItem({
  data,
  getFieldByUrn,
  filteredData,
  id,
  object,
}) {
  const dictField = getFieldByUrn(data.dictionary_urn)

  const [downloadUrl, setDownloadUrl] = useState("")

  if (!dictField) {
    return null
  }
  let value = data.valueString || data.value
  let valueArray = data.valueArray

  if (
    dictField.type === "type/date" ||
    dictField.type === "type/datetime" ||
    dictField.type === "type/time"
  ) {
    value = new Date(data.value).toLocaleDateString()
  }

  const isDocument = dictField.type === "type/document"

  const renderDocumentLink = (item) => {
    const path = getDocumentPath(item)
    const label = getDocumentLabel(path)
    const DocIcon = getDocumentIcon(path)
    return (
      <Link
        component="button"
        type="button"
        disabled={!path}
        onClick={() => {
          if (!path) return
          setDownloadUrl("loading")
          getDownloadUrl(id, path).finally(() => setDownloadUrl(""))
        }}
        sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
        <Box
          component="span"
          sx={{
            width: 40,
            height: 50,
            borderRadius: 2,
            bgcolor: "grey.200",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}>
          <DocIcon color="primary" />
        </Box>
        {label}
      </Link>
    )
  }

  if (isDocument && (!valueArray || valueArray.length === 0)) {
    value = renderDocumentLink(data)
  }

  return (
    <>
      {valueArray && valueArray.length > 0 && (
        // <div
        //   style={{
        //     ...styles["text-xs"],
        //     ...styles["bold"],
        //     ...styles["underline"],
        //     ...styles["mb-4"],
        //     ...styles["capitalize"]
        //   }}>
        //   {dictDefinition?.base_text}
        // </div>
        <Typography variant="subtitle2" gutterBottom>
          {dictField?.label}
        </Typography>
      )}
      <Box
        key={data.urn}
        style={{
          ...styles.field,
          ...styles["with-copy"],
          ...{ display: "flex", flexDirection: "row", flexWrap: "wrap" }
        }}>
        <Typography variant="body2" gutterBottom sx={{ flexBasis: 150 }}>
          {filteredData.length === 1 && object.tags.length > 0
            ? object.tags[0].label
            : dictField?.label}
          :
        </Typography>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            flexGrow: 1
          }}>
          {valueArray?.map((valueObject) => {
            return (
              <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    flexGrow: 1
                  }}
                  key={valueObject.urn}>
                  {valueObject.data?.map((nestedValue, idx) => {
                    const displayValue = isDocument ? (
                      renderDocumentLink(nestedValue)
                    ) : (
                      <>
                        {idx > 0 ? " " : ""}
                        {nestedValue.valueString || nestedValue.value}
                      </>
                    )
                    return (
                      <Typography
                        variant="subtitle2"
                        component="div"
                        gutterBottom
                        style={{
                          ...styles["font-semibold"],
                          whiteSpace: "pre-wrap"
                        }}
                        key={nestedValue.urn}>
                        {displayValue}
                      </Typography>
                    )
                  })}
                </div>
              </>
            )
          })}

          {(!valueArray || valueArray.length === 0) && (
            <Typography
              variant="subtitle2"
              gutterBottom
              style={{ whiteSpace: "pre-wrap" }}>
              {value}
            </Typography>
          )}
        </div>

        {dictField.type === "type/document" && downloadUrl !== "" ? (
          <div>
            <svg
              style={{
                width: "16px",
                height: "16px",
                marginLeft: "8px",
                cursor: "pointer"
              }}
              width="800px"
              height="800px"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M7.706 0.290 C 7.484 0.362,7.356 0.490,7.294 0.699 C 7.259 0.816,7.253 1.088,7.253 2.508 C 7.253 4.389,7.251 4.365,7.443 4.557 C 7.700 4.813,8.300 4.813,8.557 4.557 C 8.749 4.365,8.747 4.389,8.747 2.508 C 8.747 0.688,8.744 0.656,8.596 0.480 C 8.472 0.333,8.339 0.284,8.040 0.276 C 7.893 0.272,7.743 0.278,7.706 0.290 M2.753 2.266 C 2.595 2.338,2.362 2.566,2.281 2.728 C 2.197 2.897,2.193 3.085,2.269 3.253 C 2.343 3.418,4.667 5.750,4.850 5.843 C 5.109 5.976,5.375 5.911,5.643 5.649 C 5.907 5.391,5.977 5.111,5.843 4.850 C 5.750 4.667,3.418 2.343,3.253 2.269 C 3.101 2.200,2.901 2.199,2.753 2.266 M12.853 2.282 C 12.730 2.339,12.520 2.536,11.518 3.541 C 10.597 4.464,10.316 4.762,10.271 4.860 C 10.195 5.025,10.196 5.216,10.272 5.378 C 10.342 5.528,10.572 5.764,10.727 5.845 C 10.884 5.927,11.117 5.926,11.280 5.843 C 11.447 5.757,13.757 3.447,13.843 3.280 C 13.926 3.118,13.927 2.884,13.846 2.729 C 13.764 2.572,13.552 2.364,13.392 2.283 C 13.213 2.192,13.048 2.192,12.853 2.282 M0.699 7.292 C 0.404 7.385,0.258 7.620,0.258 7.999 C 0.259 8.386,0.403 8.618,0.698 8.706 C 0.816 8.741,1.079 8.747,2.508 8.747 C 3.997 8.747,4.196 8.742,4.318 8.702 C 4.498 8.644,4.644 8.498,4.702 8.318 C 4.788 8.053,4.745 7.677,4.608 7.491 C 4.578 7.451,4.492 7.384,4.417 7.343 L 4.280 7.267 2.547 7.261 C 1.152 7.257,0.791 7.263,0.699 7.292 M11.745 7.278 C 11.622 7.308,11.452 7.411,11.392 7.492 C 11.255 7.677,11.212 8.053,11.298 8.318 C 11.356 8.498,11.502 8.644,11.682 8.702 C 11.804 8.742,12.003 8.747,13.492 8.747 C 14.921 8.747,15.184 8.741,15.302 8.706 C 15.597 8.618,15.741 8.386,15.742 7.999 C 15.742 7.614,15.595 7.383,15.290 7.291 C 15.187 7.260,14.864 7.254,13.496 7.256 C 12.578 7.258,11.790 7.268,11.745 7.278 M4.853 10.282 C 4.730 10.339,4.520 10.536,3.518 11.541 C 2.597 12.464,2.316 12.762,2.271 12.860 C 2.195 13.025,2.196 13.216,2.272 13.378 C 2.342 13.528,2.572 13.764,2.727 13.845 C 2.884 13.927,3.117 13.926,3.280 13.843 C 3.447 13.757,5.757 11.447,5.843 11.280 C 5.926 11.118,5.927 10.884,5.846 10.729 C 5.764 10.572,5.552 10.364,5.392 10.283 C 5.213 10.192,5.048 10.192,4.853 10.282 M10.753 10.266 C 10.595 10.338,10.362 10.566,10.281 10.728 C 10.197 10.897,10.193 11.085,10.269 11.253 C 10.343 11.418,12.667 13.750,12.850 13.843 C 13.109 13.976,13.375 13.911,13.643 13.649 C 13.907 13.391,13.977 13.111,13.843 12.850 C 13.750 12.667,11.418 10.343,11.253 10.269 C 11.101 10.200,10.901 10.199,10.753 10.266 M7.745 11.277 C 7.620 11.309,7.451 11.412,7.392 11.492 C 7.254 11.678,7.253 11.691,7.253 13.489 C 7.253 14.921,7.259 15.184,7.294 15.302 C 7.382 15.597,7.615 15.741,8.000 15.741 C 8.385 15.741,8.618 15.597,8.706 15.302 C 8.768 15.090,8.767 11.875,8.704 11.690 C 8.644 11.514,8.575 11.430,8.420 11.346 C 8.310 11.286,8.246 11.271,8.057 11.264 C 7.930 11.259,7.790 11.265,7.745 11.277 "
                stroke="none"

                fill="#000000"></path>
            </svg>
          </div>
        ) : dictField.type !== "type/document" ? (
          <svg
            onClick={() => {
              if (valueArray && valueArray.length > 0) {
                copyToClipboard(
                  valueArray.map((value) => value.value).join("\r\n")
                )
              } else {
                copyToClipboard(value)
              }
            }}
            xmlns="http://www.w3.org/2000/svg"
            style={{
              width: "16px",
              height: "16px",
              marginLeft: "8px",
              cursor: "pointer"
            }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ) : null}
      </Box>
    </>
  )
}

// Object template urns are either a bare code ("template/passport") or
// "<instanceId>/template/passport" — same rule as the app's splitTemplateUrn.
const getTemplateCode = (urn) => {
  if (!urn) return null
  const split = urn.split("/")
  return split[0] === "template" ? urn : split.slice(1).join("/")
}

function Data({
  collection,
  getCollectionByUrn,
  getFieldByUrn,
  getTemplateByUrn,
  isPerson,
  labels,
}) {
  const { id } = useLoaderData()
  let collectedObjects = []

  // Optional plain-text identifier the sender set for this person/vault —
  // not the person/vault's own name, just a hint for what this section is,
  // same idea as a provider's custom field label. Empty unless set.
  const customName = labels?.find((l) => l.vault_urn === collection.urn)
    ?.custom_name

  // Sub-templates kept inline on a person/vault (e.g. a passport or driving
  // licence) arrive as extra objects whose template differs from the
  // parent's. Group those under their own heading so they don't read as
  // part of the person; the parent's own objects stay ungrouped, first.
  const parentCode =
    getTemplateCode(collection.template_urn) ||
    (isPerson ? "template/person" : null)
  const ownObjects = []
  const subTemplateGroups = []
  for (const object of collection.objects || []) {
    const code = getTemplateCode(object.template_urn)
    if (!parentCode || !code || code === parentCode) {
      ownObjects.push(object)
      continue
    }
    // One group per sub-template instance, so two passports stay apart.
    let group = subTemplateGroups.find((g) => g.urn === object.template_urn)
    if (!group) {
      const template = getTemplateByUrn(code)
      group = {
        urn: object.template_urn,
        label: template?.name || template?.label || code.split("/").pop(),
        objects: []
      }
      subTemplateGroups.push(group)
    }
    group.objects.push(object)
  }

  const renderObject = (object) => {
    const dict = getCollectionByUrn(object.dictionary_urn)
    if (!dict) {
      return null
    }
    let hasLabel = false
    if (collectedObjects.indexOf(dict.urn) > 0) {
      hasLabel = true
    }
    collectedObjects.push(dict.urn)

    let filteredData = collection.data
      ?.filter((d) => object.children.indexOf(d.urn) >= 0)
      ?.sort((a, b) => {
        //const find the sequence from the dictionaty
        let dictA = dict.fields.find((c) => a.dictionary_urn === c.reference_urn)
        if (!dictA) {
          dictA = { sequence: 'ZZZZZ' }
        }
        let dictB = dict.fields.find((c) => b.dictionary_urn === c.reference_urn)
        if (!dictB) {
          dictB = { sequence: 'ZZZZZ' }
        }
        if (dictA.sequence > dictB.sequence) {
          return 1
        }
        if (dictA.sequence < dictB.sequence) {
          return -1
        }
        return 0
      })
    return (
      <Box item xs key={object.urn}>
        <div
          style={{ display: "flex", flexDirection: "column" }}>
          {!hasLabel && (
            <div style={styles.fieldGroup}>
              <div style={styles.fieldName}></div>
            </div>
          )}
          {filteredData.length > 1 && object.tags.length > 0 && (
            <div
              style={{
                ...styles["text-xs"],
                ...styles["bold"],
                ...styles["underline"],
                ...styles["mb-4"],
                ...styles["capitalize"]
              }}>
              {object.tags[0].label} {dict.label}
            </div>
          )}

          {filteredData.map((data) => {
            return (
              <DataItem
                key={data.urn}
                data={data}
                getFieldByUrn={getFieldByUrn}
                filteredData={filteredData}
                id={id}
                dict={dict}
                object={object}
              />
            )
          })}
        </div>
      </Box>
    )
  }

  return (
    <Box key={collection.urn} mt={2}>
      <div
        key={collection.urn}
        style={{
          borderWidth: 1,
          borderStyle: "solid",
          boxShadow: "0 24px 44px 0 var(--shadow-card-short)",
          borderRadius: 8,
          flexDirection: "column",
          alignItems: "center",
          padding: 13
        }}>
        <div style={styles.fieldGroup}>
          <div style={styles.fieldName}>{collection.name}</div>
          {customName && (
            <div style={styles.customName}>{customName}</div>
          )}
        </div>
        {ownObjects.map(renderObject)}
        {subTemplateGroups.map((group) => (
          <Box key={group.urn} style={styles.subTemplate}>
            <div style={styles.subTemplateName}>{group.label}</div>
            {group.objects.map(renderObject)}
          </Box>
        ))}
      </div>
    </Box>
  )
}

function DisplayData() {
  const navigate = useNavigate()
  const { id } = useLoaderData()
  const {
    loading: dictLoading,
    getCollectionByUrn,
    getFieldByUrn,
    getTagByUrn,
    getTemplateByUrn
  } = useDictionary()


  const [showError, setShowError] = useState(false)
  const [error, setError] = useState("")
 
  const [message, setMessage] = useState("")
  const [people, setPeople] = useState([])
  const [vaults, setVaults] = useState([])
  const [labels, setLabels] = useState([])

  // Same condition the Close button uses to decide the request still needs
  // closing off: once there's a message or an error it is already finished.
  useCancelOnClose(id, !message && !error)

  useInterval((cancel) => {
    if (!dictLoading) {

      getData(id).then((result) => {
        let showError = false
        if (result?.error && result.message) {
          setMessage(null)
          setPeople([])
          setVaults([])
          setLabels([])

          setError(result.message || "Something went wrong. Please try again.")
          showError = true
        } else if (result?.data?.cancelled) {
          setMessage("User cancelled the request")
          setPeople([])
          setVaults([])
          setLabels([])
          cancel()
        } else if (result?.data?.expired) {
          setMessage("Request expired")
          setPeople([])
          setVaults([])
          setLabels([])
          cancel()
        } else {
          if (result?.complete) {
            setMessage(
              "The GetMyDetails user has closed the connection.  Data will be available for a limited time."
            )
            cancel()
          } else {
            setMessage(null)
          }
          if (result?.data?.vault) {
            //first of all lets get the poeple
            const people = result.data.vault.people
            const vaults = result.data.vault.vaults
            setPeople(people || [])
            setVaults(vaults || [])
            setLabels(result.data.vault.labels || [])
          }
        }

          // if (result.data?.vault?.objects.length > 0) {
          //   //group objects by field

          //   const fields = []
          //   for (const field of result.data.vault.objects) {
          //     let existing = fields.findIndex((f) => f.field === field.field)
          //     if (existing < 0) {
          //       const dict = getCollectionByUrn(
          //         field.field.split("/").slice(1).join("/")
          //       )
          //       existing =
          //         fields.push({
          //           field: field.field,
          //           dictionary: dict,
          //           instances: []
          //         }) - 1
          //     }

          //     let instanceExisting = fields[existing].instances.findIndex(
          //       (i) => i.instance === field.instance
          //     )
          //     if (instanceExisting < 0) {
          //       const dict = getInstanceByUrn(
          //         field.instance.split("/").slice(1).join("/")
          //       )
          //       instanceExisting =
          //         fields[existing].instances.push({
          //           instance: field.instance,
          //           dictionary: dict,
          //           values: [],
          //           tags: field.tags
          //         }) - 1
          //     }

          //     const dict = getDefinitioneByUrn(field.field_definition)
          //     fields[existing].instances[instanceExisting].values.push({
          //       definition: field.field_definition,
          //       dictionary: dict,
          //       value: field.value
          //     })
          //   }
          // }
        

        setShowError(showError)
      })
    }
  }, 1000)

  const cancel = () => {
    navigate(`/`)
  }

  const finished = () => {
    if (!message && !error) {
      finishRequest(id)
    }
    navigate("/")
  }

  return (
    <Container maxWidth="md">
      <Box sx={{mt: 2}} display="flex" justifyContent="center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          id="Layer_2"
          data-name="Layer 2"
          viewBox="0 0 2048 298.95"
          style={{ maxWidth: 350 }}>
          <defs>
            <linearGradient
              id="linear-gradient"
              x1="0"
              x2="298.95"
              y1="149.48"
              y2="149.48"
              gradientUnits="userSpaceOnUse">
              <stop offset="0.5" stopColor="#500778" />
              <stop offset="0.71" stopColor="#680e88" />
              <stop offset="1" stopColor="#87189d" />
            </linearGradient>
            <style>{".cls-2{fill:#1d1d1b}"}</style>
          </defs>
          <g id="Layer_1-2" data-name="Layer 1">
            <path
              d="M467.63 240.02c-13.75 0-26.52-2.41-38.3-7.23-11.79-4.82-22.14-11.52-31.07-20.09s-15.85-18.7-20.76-30.4c-4.91-11.69-7.37-24.42-7.37-38.17s2.45-26.47 7.37-38.17c4.91-11.69 11.69-21.83 20.36-30.4 8.66-8.57 18.84-15.22 30.54-19.96 11.69-4.73 24.24-7.1 37.64-7.1 9.46 0 18.79 1.34 27.99 4.02 9.19 2.68 17.54 6.43 25.05 11.25 7.5 4.82 13.57 10.27 18.21 16.34l-13.93 14.2c-5-5.18-10.63-9.64-16.88-13.39a90.46 90.46 0 0 0-19.69-8.84c-6.88-2.14-13.71-3.21-20.49-3.21-10.54 0-20.32 1.88-29.33 5.63-9.02 3.75-16.92 8.97-23.71 15.67-6.79 6.7-12.05 14.64-15.8 23.84s-5.63 19.24-5.63 30.13 1.92 20.4 5.76 29.6c3.84 9.2 9.28 17.24 16.34 24.11 7.05 6.88 15.22 12.19 24.51 15.94 9.28 3.75 19.29 5.62 30 5.62 8.39 0 16.29-1.25 23.71-3.75 7.41-2.5 13.88-6.03 19.42-10.58 5.53-4.55 9.82-9.86 12.86-15.94 3.03-6.07 4.55-12.59 4.55-19.55v-9.38l4.29 5.36h-65.89v-20.63h82.5c.18 1.43.36 3.08.54 4.96a118.303 118.303 0 0 1 .53 10.85c0 11.61-2.14 22.28-6.43 32.01-4.29 9.74-10.22 18.13-17.81 25.18-7.59 7.06-16.39 12.5-26.38 16.34-10 3.84-20.89 5.76-32.68 5.76ZM642.81 240.02c-13.93 0-26.3-3.03-37.1-9.11-10.81-6.07-19.24-14.55-25.31-25.45-6.07-10.89-9.11-23.39-9.11-37.5 0-10.89 1.69-20.76 5.09-29.6 3.39-8.84 8.17-16.56 14.33-23.17 6.16-6.6 13.53-11.74 22.1-15.4 8.57-3.66 17.85-5.49 27.86-5.49 9.46 0 18.08 1.7 25.85 5.09 7.77 3.39 14.47 8.08 20.09 14.06 5.63 5.98 9.87 13.08 12.72 21.3 2.85 8.22 4.11 17.24 3.75 27.05l-.27 9.38H586.29l-2.95-16.88h104.2l-4.82 4.55v-6.43c-.36-6.43-2.23-12.63-5.63-18.62-3.39-5.98-8.17-10.85-14.33-14.6s-13.53-5.63-22.1-5.63c-10 0-18.66 2.01-25.98 6.03-7.32 4.02-12.95 9.96-16.87 17.81-3.93 7.86-5.89 17.59-5.89 29.2s2.14 20.67 6.43 28.8c4.29 8.13 10.4 14.42 18.35 18.88 7.94 4.47 17.55 6.7 28.8 6.7 6.6 0 13.17-1.11 19.69-3.35 6.52-2.23 13.7-6.65 21.56-13.26l10.45 14.46c-4.11 4.11-9.02 7.73-14.73 10.85-5.72 3.13-11.97 5.63-18.75 7.5a78.302 78.302 0 0 1-20.89 2.81ZM719.42 116.81V97.52h87.06v19.29h-87.06Zm31.6 120.53V60.82h20.09v176.52h-20.09ZM839.15 237.34V49.84h20.36l72.06 117.06-9.64-.27 72.86-116.79h19.02v187.5h-20.89v-85.72c0-16.07.27-30.54.8-43.39.54-12.86 1.52-25.62 2.95-38.3l3.75 7.5-67.23 105.54h-14.46l-65.36-104.2 2.95-8.84c1.43 11.97 2.41 24.24 2.95 36.83s.8 27.55.8 44.87v85.72h-20.89ZM1084.51 298.95l27.05-65.89 1.88 19.02-70.18-154.56h22.77l47.95 107.15c1.43 2.86 2.85 6.21 4.29 10.04 1.43 3.84 2.77 7.63 4.02 11.38l-7.5-.8c1.25-3.03 2.45-6.16 3.62-9.38 1.16-3.21 2.28-6.34 3.35-9.37l42.06-109.02h23.04l-56.52 139.82-24.11 61.61h-21.7ZM1216.03 237.34V49.84h70.72c12.86 0 24.6 2.28 35.22 6.83 10.62 4.55 19.86 11.03 27.72 19.42 7.86 8.4 13.93 18.31 18.21 29.73 4.29 11.43 6.43 24.02 6.43 37.77s-2.14 26.38-6.43 37.9c-4.29 11.52-10.31 21.43-18.08 29.73-7.77 8.3-17.01 14.73-27.72 19.29-10.72 4.55-22.5 6.83-35.36 6.83h-70.72Zm20.89-16.6-2.41-3.75h50.89c9.82 0 18.88-1.74 27.19-5.22 8.3-3.48 15.44-8.48 21.43-15 5.98-6.52 10.62-14.24 13.93-23.17 3.3-8.93 4.96-18.93 4.96-30s-1.65-21.07-4.96-30-7.99-16.65-14.06-23.17c-6.07-6.52-13.22-11.52-21.43-15-8.22-3.48-17.24-5.22-27.05-5.22h-51.7l3.21-3.21v153.75ZM1468.62 240.02c-13.93 0-26.3-3.03-37.1-9.11-10.81-6.07-19.24-14.55-25.31-25.45-6.07-10.89-9.11-23.39-9.11-37.5 0-10.89 1.69-20.76 5.09-29.6 3.39-8.84 8.17-16.56 14.33-23.17 6.16-6.6 13.53-11.74 22.1-15.4 8.57-3.66 17.85-5.49 27.86-5.49 9.46 0 18.08 1.7 25.85 5.09 7.77 3.39 14.47 8.08 20.09 14.06 5.63 5.98 9.87 13.08 12.72 21.3 2.85 8.22 4.11 17.24 3.75 27.05l-.27 9.38H1412.1l-2.95-16.88h104.2l-4.82 4.55v-6.43c-.36-6.43-2.23-12.63-5.63-18.62-3.39-5.98-8.17-10.85-14.33-14.6s-13.53-5.63-22.1-5.63c-10 0-18.66 2.01-25.98 6.03-7.32 4.02-12.95 9.96-16.87 17.81-3.93 7.86-5.89 17.59-5.89 29.2s2.14 20.67 6.43 28.8c4.29 8.13 10.4 14.42 18.35 18.88 7.94 4.47 17.55 6.7 28.8 6.7 6.6 0 13.17-1.11 19.69-3.35 6.52-2.23 13.7-6.65 21.56-13.26l10.45 14.46c-4.11 4.11-9.02 7.73-14.73 10.85-5.72 3.13-11.97 5.63-18.75 7.5a78.302 78.302 0 0 1-20.89 2.81ZM1545.23 116.81V97.52h87.06v19.29h-87.06Zm31.61 120.53V60.82h20.09v176.52h-20.09ZM1710.23 240.02c-12.15 0-23.13-3.17-32.95-9.51-9.82-6.34-17.59-15-23.3-25.98-5.72-10.98-8.57-23.44-8.57-37.37s2.95-26.61 8.84-37.5 13.8-19.46 23.71-25.72c9.91-6.25 21.03-9.38 33.35-9.38 7.32 0 14.1 1.07 20.36 3.21 6.25 2.14 11.83 5.14 16.74 8.97 4.91 3.84 9.01 8.3 12.32 13.39 3.3 5.09 5.58 10.58 6.83 16.47l-5.89-2.95.8-36.16h20.09v139.82h-20.36v-34.29l5.36-3.48c-1.25 5.36-3.66 10.49-7.23 15.4-3.57 4.91-7.9 9.24-12.99 12.99s-10.81 6.7-17.14 8.84c-6.34 2.14-12.99 3.21-19.96 3.21Zm4.56-19.28c9.28 0 17.59-2.28 24.91-6.83 7.32-4.55 13.08-10.89 17.28-19.02 4.19-8.12 6.29-17.37 6.29-27.72s-2.1-19.55-6.29-27.59c-4.2-8.04-9.96-14.37-17.28-19.02-7.32-4.64-15.63-6.96-24.91-6.96s-17.81 2.32-25.05 6.96c-7.23 4.65-12.95 10.98-17.14 19.02-4.2 8.04-6.29 17.24-6.29 27.59s2.05 19.33 6.16 27.46c4.11 8.13 9.82 14.51 17.14 19.15 7.32 4.65 15.71 6.96 25.18 6.96ZM1836.93 67.25c-5 0-8.8-1.29-11.38-3.88-2.59-2.59-3.88-6.11-3.88-10.58s1.34-8.04 4.02-10.71c2.68-2.68 6.43-4.02 11.25-4.02s8.57 1.3 11.25 3.88c2.68 2.59 4.02 6.21 4.02 10.85 0 4.29-1.34 7.77-4.02 10.45-2.68 2.68-6.43 4.02-11.25 4.02Zm-9.91 170.09V97.52h20.09v139.82h-20.09ZM1891.57 237.34V39.13h20.09v198.22h-20.09ZM1997.11 240.02c-11.97 0-22.68-2.23-32.14-6.7-9.47-4.46-16.97-10.35-22.5-17.68l14.2-12.32c5.18 6.25 11.29 10.98 18.35 14.2 7.05 3.21 14.95 4.82 23.71 4.82 4.46 0 8.48-.49 12.05-1.47 3.57-.98 6.7-2.45 9.38-4.42 2.68-1.96 4.73-4.29 6.16-6.96 1.43-2.68 2.14-5.63 2.14-8.84 0-5.89-2.59-10.8-7.77-14.73-2.32-1.61-5.63-3.26-9.91-4.96-4.29-1.69-9.47-3.35-15.54-4.96-10-2.85-18.31-5.76-24.91-8.71-6.61-2.95-11.79-6.38-15.54-10.31-2.68-3.21-4.69-6.7-6.03-10.45-1.34-3.75-2.01-7.86-2.01-12.32 0-5.71 1.21-10.98 3.62-15.8s5.8-9.06 10.18-12.72c4.37-3.66 9.55-6.43 15.54-8.3 5.98-1.88 12.46-2.81 19.42-2.81 6.6 0 13.12.9 19.55 2.68 6.43 1.79 12.41 4.38 17.95 7.77 5.53 3.39 10.27 7.41 14.2 12.05l-12.32 13.39a72.798 72.798 0 0 0-11.25-9.64c-4.11-2.85-8.44-5.09-12.99-6.7a41.857 41.857 0 0 0-14.06-2.41c-4.29 0-8.26.54-11.92 1.61s-6.79 2.54-9.38 4.42c-2.59 1.88-4.55 4.15-5.89 6.83-1.34 2.68-2.01 5.63-2.01 8.84 0 2.68.54 5.09 1.61 7.23 1.07 2.14 2.59 4.02 4.55 5.63 2.5 1.97 6.03 3.8 10.58 5.49 4.55 1.7 10.22 3.44 17.01 5.22 7.86 2.14 14.51 4.33 19.96 6.56 5.44 2.23 9.95 4.87 13.53 7.9 4.82 3.57 8.26 7.68 10.31 12.32 2.05 4.65 3.08 9.91 3.08 15.8 0 7.86-2.19 14.83-6.56 20.89-4.38 6.07-10.41 10.85-18.08 14.33-7.68 3.48-16.43 5.22-26.25 5.22Z"
              className="cls-2"></path>
            <path
              d="M274.12 24.85C252.53 1.33 201.01 1 149.48 0 97.96 1 46.45 1.33 24.85 24.85 1.33 46.45 1 97.96 0 149.48c1 51.53 1.33 103.05 24.85 124.64 21.59 23.5 73.11 23.85 124.62 24.83 51.53-.98 103.05-1.33 124.64-24.83 23.5-21.59 23.85-73.11 24.83-124.64-.98-51.51-1.33-103.03-24.83-124.62ZM254.7 151.04v62.51l-80.74-80.78V247.1H44.32l49.57-49.55-49.65-49.67V129.9l58.65 58.63 8.98 9.02-9 8.98-27.85 27.85h86.21v-86.21l-27.11 27.13-9 8.98-9-9-71.89-71.99V85.28l80.78 80.9V51.86H254.7l-49.57 49.59 49.57 49.59Z"
              style={{ fill: "url(#linear-gradient)" }}></path>
          </g>
        </svg>
      </Box>


      <>
        <Box sx={{mt:1 , mb:1 }}>
          {(people.length > 0 || vaults.length > 0) && (
            <Typography align="left" component="h6" variant="h6">
              Information received from user
            </Typography>
          )}
          <Box sx={{mt: 1, mb: 1}} textAlign="center">
            <Alert sx={{ display: !message && "none" }} severity="info">
              {message}
            </Alert>
          </Box>
        </Box>

        {people.map((person) => {
          return (
            <Data
            key={person.urn}
              collection={person}
              getCollectionByUrn={getCollectionByUrn}
              getFieldByUrn={getFieldByUrn}
              getTemplateByUrn={getTemplateByUrn}
              isPerson
              labels={labels}
            />
          )
        })}

        {vaults.map((vault) => {
          return (
            <Data
            key={vault.urn}
              collection={vault}
              getCollectionByUrn={getCollectionByUrn}
              getFieldByUrn={getFieldByUrn}
              getTemplateByUrn={getTemplateByUrn}
              labels={labels}
            />
          )
        })}
      </>


      <Box sx={{mt: 1, mb: 1}}>
        <Button
          onClick={() => {
            if (error) {
              cancel()
            } else {
              finished()
            }
          }}
          type="submit"
          fullWidth
          variant="contained">
          Close
        </Button>
      </Box>
      <Box sx={{mt: 1, mb: 1}}>
        <Alert sx={{ display: !showError && "none" }} severity="error">
          {error}
        </Alert>
      </Box>
      <>
        {(people.length > 0 || vaults.length > 0) && (
          <Box sx={{mt: 1, mb: 1}}>
            <Typography align="left" component="h6" variant="body2">
              Please make sure you copy this information before closing the tab
              as once you close it, the information will be gone.
            </Typography>
          </Box>
        )}

        <Box sx={{mt: 1, mb: 1}}>
          <Typography align="left" variant="body2">
            GetMyDetails can help businesses of all sizes get accurate
            information from their customers fast. Click{" "}
            <a
              href="https://getmydetails.com"
              target="_blank"
              rel="noopener noreferrer">
              here
            </a>{" "}
            to learn more about using GetMyDetails to turbo charge your
            business.
          </Typography>
        </Box>
      </>
    </Container>
  )
}
export default DisplayData

const styles = {
  underline: {
    textDecoration: "underline"
  },

  "mb-4": {
    marginTop: "8px"
  },

  "font-semibold": {
    fontWeight: "500"
  },

  "text-xs": {
    fontSize: "12px"
  },

  bold: {
    fontWeight: "500"
  },
  "btn-wrap": {
    paddingTop: "1rem"
  },
  "btn-wrapper": {
    width: "100%",
    columnGap: "12px"
  },

  capitalize: {
    textTransform: "capitalize"
  },

  flex: {
    display: "flex"
  },
  field: {
    // marginBottom: "12px"
  },

  fieldGroup: {
    minWidth: "300px"
  },

  fieldName: {
    color: "#500778",
    textTransform: "capitalize",
    fontWeight: "bold"
    // marginBottom: "0.75rem"
  },

  customName: {
    color: "#666",
    fontSize: "0.85rem"
  },

  subTemplate: {
    marginTop: "12px",
    paddingTop: "8px",
    borderTop: "1px solid #e0e0e0"
  },

  subTemplateName: {
    color: "#500778",
    fontSize: "0.9rem",
    fontWeight: "bold",
    textTransform: "capitalize",
    marginBottom: "4px"
  },

  formGroup: {
    marginBottom: "1.5rem"
  },

  "with-copy": {
    display: "flex",
    alignItems: "center"
  },
  "with-copy-svg": {
    marginLeft: "8px",
    cursor: "pointer"
  },
  "submit-button": {
    display: "block",
    width: "100%",
    height: "48px",
    color: "white",
    background: "#500778",
    borderRadius: "12px",
    fontSize: "16px",
    cursor: "pointer",
    border: "none",
    transition: "all 0.2s"
  }
}
